import servicesConfig, { CustomAxiosHeaders } from '../config/services'
import { requests } from '.'
import { AxiosResponse } from 'axios'

// Configure request setup
const serviceName = 'LocationService'
const host = servicesConfig.Locations
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Helper function for waiting
function wait(ms: number, value: any) {
  return new Promise(resolve => setTimeout(resolve, ms, value));
}

// Define service object with methods
const LocationServices = {
  ids: (ids: any) => request().post(`/ids`, { ids }).then(responseBody),
  getTags: (locationId: string) => request().get(`/${locationId}/tags`).then(responseBody),
  search: (companyId: string, deleted: boolean, status?: string, skip?: number, limit?: number, query?: string) =>
    request().get(
      `/search?deleted=${deleted}&companyId=${companyId}${status ? `&status=${status}` : ''
      }${skip ? `&skip=${skip}` : ''}${limit ? `&limit=${limit}` : ''}${query ? `&query=${query}` : ''
      }`
    ).then(responseBody),
  searchQuery: (companyId: string, deleted: boolean, query: string) =>
    request().get(
      `/search?deleted=${deleted}&companyId=${companyId}&query=${query}&limit=50`
    ).then(responseBody),
  read: (locationId: string) => request().get(`/${locationId}`).then(responseBody),
  verifyDomain: (domain: string, companyId: string, locationId: string) => request().get(`/verify/domain/${domain}?companyId=${companyId}&locationId=${locationId}`).then(responseBody),
  
  CustomValues: {
    create: (locationId: string, data: { [key: string]: any }) =>
      request().post(`/${locationId}/customValues/`, data).then(responseBody),
    read: (locationId: string, customValueId: string) =>
      request().get(`/${locationId}/customValues/${customValueId}`).then(responseBody),
    update: (
      locationId: string,
      customValueId: string,
      data: { [key: string]: any }
    ) =>
      request().put(`/${locationId}/customValues/${customValueId}`, data).then(responseBody),
    delete: (locationId: string, customValueId: string) =>
      request().delete(`/${locationId}/customValues/${customValueId}`).then(responseBody),
    list: (locationId: string) =>
      request().get(`/${locationId}/customValues/`).then(responseBody),
    restoreCustomValue: (customValueId: string, locationId: string) =>
      request().put(`/${locationId}/customValues/${customValueId}/restore`, {}).then(responseBody),
  },

  CustomFields: {
    create: (locationId: string, data: { [key: string]: any }) =>
      request().post(`/${locationId}/customFields/`, data).then(responseBody),
    read: (locationId: string, customFieldId: string) =>
      request().get(`/${locationId}/customFields/${customFieldId}`).then(responseBody),
    update: (
      locationId: string,
      customFieldId: string,
      data: { [key: string]: any }
    ) =>
      request().put(`/${locationId}/customFields/${customFieldId}`, data).then(responseBody),
    delete: (locationId: string, customFieldId: string) =>
      request().delete(`/${locationId}/customFields/${customFieldId}`).then(responseBody),
    search: (locationId: string, parentId='custom', skip=0, limit=10, documentType = 'field', query="", model="contact", includeStandards=true) =>
      request().get(`/${locationId}/customFields/search?parentId=${parentId}&skip=${skip}&limit=${limit}&documentType=${documentType}&model=${model}&query=${query}&includeStandards=${includeStandards}`).then(responseBody),
    move: (locationId: string, parentId: string, fieldIds: string[]) => 
      request().put(`/${locationId}/customFields/bulk/parent`, {parentId, fieldIds}).then(responseBody),
    updatePosition: (
      locationId: string,
      customFieldId: string,
      data: { [key: string]: any }
    ) =>
      request().put(`/${locationId}/customFields/${customFieldId}/position`, data).then(responseBody),
    restoreCustomField: (customFieldId: string, locationId: string) =>
      request().post(`/${locationId}/customFields/${customFieldId}/restore`, {}).then(responseBody),
  },
  
  Tasks: {
    list: (queryData: { [key: string]: any }) =>
      request().get(`/${queryData.locationId}/tasks?isLocation=${queryData.isLocation}${queryData.userId ? `&userId=${queryData.userId}` : ''}${queryData.contactId ? `&contactId=${queryData.contactId}` : ''}`).then(responseBody),
    read: (locationId: string, taskId: string) =>
      request().get(`/${locationId}/tasks/${taskId}`).then(responseBody),
    delete: (locationId: string, taskId: string) =>
      request().delete(`/${locationId}/tasks/${taskId}`).then(responseBody),
    accountTaskUpdate: (locationId: string, taskId: string, queryData: { [key: string]: any }) =>
      request().put(`/${locationId}/tasks/${taskId}`, queryData).then(responseBody),
    accountTaskCreate: (locationId: string, queryData: { [key: string]: any }) =>
      request().post(`/${locationId}/tasks`, queryData).then(responseBody),
  },
  
  Notes: {
    list: (locationId: string) =>
      request().get(`/${locationId}/notes`).then(responseBody),
    read: (locationId: string, noteId: string) =>
      request().get(`/${locationId}/notes/${noteId}`).then(responseBody),
    create: (locationId: string, queryData: { [key: string]: any }) =>
      request().post(`/${locationId}/notes`, queryData).then(responseBody),
    update: (locationId: string, noteId: string, queryData: { [key: string]: any }) =>
      request().put(`/${locationId}/notes/${noteId}`, queryData).then(responseBody),
    delete: (locationId: string, noteId: string) =>
      request().delete(`/${locationId}/notes/${noteId}`).then(responseBody),
  },
  
  Tags: {
    list: (locationId: string, skip: number, limit: number, search: string) =>
      request().get(`/${locationId}/tags?&skip=${skip}&limit=${limit}&search=${search}`).then(responseBody),
    read: (locationId: string, tagId: string) =>
      request().get(`/${locationId}/tags/${tagId}`).then(responseBody),
    create: (locationId: string, queryData: { [key: string]: any }) =>
      request().post(`/${locationId}/tags`, queryData).then(value => wait(3000, value)),
    update: (locationId: string, tagId: string, queryData: { [key: string]: any }) =>
      request().put(`/${locationId}/tags/${tagId}`, queryData).then(value => wait(3000, value)),
    delete: (locationId: string, tagId: string) =>
      request().delete(`/${locationId}/tags/${tagId}`).then(value => wait(3000, value)),
    bulkDelete: (locationId: string, tags: []) =>
      request().post(`/${locationId}/tags/bulk/delete`, { tagIds: tags }).then(value => wait(3000, value)),
    restoreTag: (tagId: string, locationId: string) => 
      request().put(`/${locationId}/tags/${tagId}/restore`, {}).then(responseBody),
  },
  
  Lab: {
    addFeedback: (locationId: string, body: { [key: string]: any }) =>
      request().post(`/${locationId}/labs/feedbacks`, body).then(responseBody),
    getFeatureFlags: (locationId: string) =>
      request().get(`/${locationId}/labs/featureFlags/`).then(responseBody),
    upsertFeatureFlag: (locationId: string, body: { [key: string]: any }) =>
      request().put(`/${locationId}/labs/featureFlags`, body).then(responseBody),
  },
  
  ConversationChannels: {
    list: (locationId: string, type: string) =>
      request().get(`/${locationId}/conversationChannels/${type}`).then(responseBody),
    update: (locationId: string, queryData: { [key: string]: any }) =>
      request().put(`/${locationId}/conversationChannels`, queryData).then(responseBody),
  },
  
  OptOutMessage: {
    toggleOptOutLanguageForLocation: (locationIds: string[], companyId: string) =>
      request().put(`/smsOptOut/toggle`, {
        locationIds,
        companyId
      }).then(responseBody),

    updateOptOutLanguage: (locationIds: string[], companyId: string, optOutMessage: string) =>
      request().put(`/smsOptOut/message`, {
        locationIds,
        companyId,
        optOutMessage
      }).then(responseBody),
  },
  
  SenderInfo: {
    updateSenderInfo: (locationId: string, companyId: string, senderIdPosition: string, senderInfoMessage: string, senderId: string, toggle: boolean) =>
      request().put(`/${locationId}/smsSenderInfo`, {
        companyId,
        senderIdPosition,
        senderInfoMessage,
        senderId,
        toggle,
      }).then(responseBody)
  }
}

export default LocationServices
